import { type ReactNode } from "react"
import { Link, useLocation, useSearchParams } from "react-router"
import { motion } from "motion/react"
import {
  AlertTriangle,
  ArrowRight,
  Building07,
  CurrencyDollar,
} from "@untitledui/icons"
import {
  useEstimatePrerequisites,
  type EstimateBlocker,
} from "@/lib/estimates/use-estimate-prerequisites"

// Full-page takeover that wraps estimate-creation routes. If the user's team
// is missing a prerequisite (no body shop / fees not reviewed), the children
// are replaced with a contextual screen that explains *why* and deep-links
// to the exact settings tab where the user can fix it.
//
// Both blockers are hard — there's no "Continue anyway." Continuing produces
// either an undeliverable estimate (no body shop) or wrong pricing
// (placeholder fees), which is exactly what the gate exists to prevent.
//
// We preserve the source URL via `?return=…` so settings actions can drop
// the user back where they were headed once the prerequisite is satisfied.

interface BlockerCopy {
  icon: typeof AlertTriangle
  title: string
  subtitle: string
  primaryLabel: string
  primaryHref: string
}

const COPY: Record<EstimateBlocker, BlockerCopy> = {
  fees: {
    icon: CurrencyDollar,
    title: "Set your shop rates first",
    subtitle:
      "Estimates price using your labor rates — and right now you're on placeholder defaults. Set yours so the numbers you send out are real.",
    primaryLabel: "Set rates",
    primaryHref: "/settings?tab=fees",
  },
  bodyshop: {
    icon: Building07,
    title: "Connect a body shop to start estimating",
    subtitle:
      "Estimates need a partner shop to receive them. Connect one — you can change or add more anytime — and you'll be able to start estimating.",
    primaryLabel: "Connect a body shop",
    primaryHref: "/settings?tab=bodyshops",
  },
}

export function EstimatePrerequisiteGate({ children }: { children: ReactNode }) {
  const { isLoading, ready, blocker } = useEstimatePrerequisites()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  if (isLoading) return null
  if (ready || !blocker) return <>{children}</>

  const copy = COPY[blocker]
  const Icon = copy.icon

  // Forward `?return=…` (or current location) so settings can return the user
  // here once they've completed the prerequisite. We layer query params so
  // the existing settings deep-link (`?tab=…`) still applies.
  const returnUrl = searchParams.get("return") ?? `${location.pathname}${location.search}`
  const primaryHref = appendReturnParam(copy.primaryHref, returnUrl)

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-zinc-50 to-white">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-[-200px] size-[680px] rounded-full bg-amber-200/30 blur-3xl" />
        <div className="absolute top-1/2 -left-40 size-[440px] rounded-full bg-zinc-100/60 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(228 228 231 / 0.55) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <div className="relative flex min-h-screen flex-col items-center justify-center p-4 md:p-10">
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 30, mass: 0.9 }}
          className="w-full max-w-xl"
        >
          <div className="rounded-3xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/[0.07] overflow-hidden p-7 md:p-10">
            <div className="flex flex-col items-center text-center">
              <div className="size-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 grid place-items-center shadow-lg shadow-amber-600/25 mb-5">
                <Icon className="size-7 text-white" strokeWidth={2} aria-hidden />
              </div>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700 mb-2">
                Quick setup needed
              </p>
              <h1 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
                {copy.title}
              </h1>
              <p className="text-[15px] text-zinc-500 mt-3 leading-relaxed max-w-md">
                {copy.subtitle}
              </p>
            </div>

            {blocker === "fees" && (
              <div className="mt-6 rounded-xl bg-amber-50/60 border border-amber-200/70 px-4 py-3 flex items-start gap-3">
                <AlertTriangle className="size-4 text-amber-600 mt-0.5 shrink-0" aria-hidden />
                <p className="text-[12.5px] text-amber-900/85 leading-relaxed">
                  <span className="font-semibold">Heads up:</span> placeholder rates won't
                  match your actual pricing. Customers won't see numbers you'd stand
                  behind until you set yours.
                </p>
              </div>
            )}

            <div className="mt-7 flex flex-col items-stretch gap-2.5 sm:flex-row sm:items-center sm:justify-center">
              <Link
                to={primaryHref}
                className="h-12 px-7 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[15px] font-semibold shadow-lg shadow-emerald-600/25 hover:shadow-emerald-600/40 active:scale-[0.99] transition-all duration-200"
              >
                {copy.primaryLabel}
                <ArrowRight className="size-[18px]" aria-hidden />
              </Link>
            </div>
          </div>

          <p className="text-center text-[12px] text-zinc-400 mt-5">
            Returning here automatically once you finish.
          </p>
        </motion.div>
      </div>
    </div>
  )
}

function appendReturnParam(href: string, returnUrl: string): string {
  const sep = href.includes("?") ? "&" : "?"
  return `${href}${sep}return=${encodeURIComponent(returnUrl)}`
}
